import React, { useState, useEffect } from 'react';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { Bell, Mail, Clock, CheckCircle, XCircle, AlertCircle, Settings } from 'lucide-react';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';

export default function NotificationsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Real-time notifications
  const { 
    notifications: realtimeNotifications, 
    unreadCount, 
    isConnected,
    markAsRead, 
    clearAll 
  } = useRealtimeNotifications();

  const [localNotifications, setLocalNotifications] = useState([]);

  // Merge real-time notifications with existing ones
  useEffect(() => {
    if (realtimeNotifications?.length > 0) {
      setLocalNotifications(prev => {
        const merged = [...realtimeNotifications, ...prev];
        // Remove duplicates by id
        const unique = merged?.filter((notif, index, self) =>
          index === self?.findIndex(n => n?.id === notif?.id)
        );
        return unique?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      });
    }
  }, [realtimeNotifications]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const tenantId = user?.user_metadata?.tenant_id;
      
      if (activeTab === 'logs') {
        const logsData = await notificationService?.getLogs(tenantId, {
          status: filterStatus !== 'all' ? filterStatus : undefined
        });
        setLogs(logsData);
      } else if (activeTab === 'preferences') {
        const prefsData = await notificationService?.getPreferences(tenantId);
        setPreferences(prefsData);
      } else if (activeTab === 'templates') {
        const templatesData = await notificationService?.getTemplates(tenantId);
        setTemplates(templatesData);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePreference = async (preferenceId, currentEnabled) => {
    try {
      await notificationService?.updatePreference(preferenceId, {
        enabled: !currentEnabled
      });
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleToggleTemplate = async (templateId, currentActive) => {
    try {
      await notificationService?.updateTemplate(templateId, {
        isActive: !currentActive
      });
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatNotificationType = (type) => {
    return type?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 relative">
      <ComponentIdBadge
        id="pg-notifications-main"
        name="Notifications Main Page"
        description="Email notification logs, preferences, and template management"
      />
      <div className="mb-8 relative">
        <ComponentIdBadge
          id="hdr-notifications-title"
          name="Notifications Page Header"
          description="Page title and description"
        />
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Bell className="h-8 w-8 text-blue-600" />
          Email Notifications
        </h1>
        <p className="text-gray-600 mt-2">
          Manage your automated email notifications, templates, and preferences
        </p>
      </div>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('logs')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logs' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="inline h-4 w-4 mr-2" />
            Notification Logs
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'preferences' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="inline h-4 w-4 mr-2" />
            Preferences
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="inline h-4 w-4 mr-2" />
            Email Templates
          </button>
        </nav>
      </div>
      {/* Notification Logs Tab */}
      {activeTab === 'logs' && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <div className="flex gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-lg ${
                  filterStatus === 'all' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterStatus('sent')}
                className={`px-4 py-2 rounded-lg ${
                  filterStatus === 'sent' ?'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sent
              </button>
              <button
                onClick={() => setFilterStatus('failed')}
                className={`px-4 py-2 rounded-lg ${
                  filterStatus === 'failed' ?'bg-red-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Failed
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs?.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No notification logs found
                      </td>
                    </tr>
                  ) : (
                    logs?.map((log) => (
                      <tr key={log?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(log?.status)}
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {log?.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {formatNotificationType(log?.notificationType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{log?.recipientEmail}</div>
                          {log?.customerName && (
                            <div className="text-xs text-gray-500">{log?.customerName}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{log?.subject}</div>
                          {log?.errorMessage && (
                            <div className="text-xs text-red-600 mt-1">{log?.errorMessage}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log?.sentAt
                            ? new Date(log.sentAt)?.toLocaleString()
                            : new Date(log.createdAt)?.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="space-y-4">
          {preferences?.map((pref) => (
            <div
              key={pref?.id}
              className="bg-white rounded-lg shadow p-6 flex items-center justify-between"
            >
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {formatNotificationType(pref?.notificationType)}
                </h3>
                {pref?.templateName && (
                  <p className="text-sm text-gray-600 mt-1">
                    Template: {pref?.templateName}
                  </p>
                )}
                {pref?.sendHoursBefore && (
                  <p className="text-xs text-gray-500 mt-1">
                    Send {pref?.sendHoursBefore} hours before
                  </p>
                )}
              </div>
              <button
                onClick={() => handleTogglePreference(pref?.id, pref?.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  pref?.enabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    pref?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {templates?.map((template) => (
            <div
              key={template?.id}
              className="bg-white rounded-lg shadow p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    {template?.name}
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        template?.category === 'appointment' ?'bg-blue-100 text-blue-800'
                          : template?.category === 'invoice' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {template?.category}
                    </span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Subject: {template?.subject}
                  </p>
                </div>
                <button
                  onClick={() => handleToggleTemplate(template?.id, template?.isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    template?.isActive ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      template?.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {template?.variables && template?.variables?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Available Variables:</p>
                  <div className="flex flex-wrap gap-2">
                    {template?.variables?.map((variable) => (
                      <span
                        key={variable}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-mono"
                      >
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}